import React, { useState, useEffect, useMemo } from 'react';
import { venueDetailsMapping } from '../misc/venueDetailsMapping';
import '../styles/VenueCard.css';

// ─── HELPERS ─────────────────────────────────────────────────────────────

// 0=Sunday … 6=Saturday
function firstWeekdayOfMonth(year, monthIndex) {
  return new Date(year, monthIndex, 1).getDay();
}

// days in that month
function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

// all {year, monthIndex} between two ISO dates
function monthsInRange(startDateStr, endDateStr) {
  const start = new Date(startDateStr);
  const end   = new Date(endDateStr);
  const months = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), 1);

  while (cur <= end) {
    months.push({ year: cur.getFullYear(), monthIndex: cur.getMonth() });
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

const getMatchColor = score => {
  if (score >= 80) return '#28a745';
  if (score >= 60) return '#ffc107';
  return '#dc3545';
};

const VenueCard = ({
  venue,
  matches,
  startDate,    // preferences.startDate string (e.g. '2025-04-20')
  endDate,      // preferences.endDate   string
  onSelectDate,
}) => {
  const details   = venueDetailsMapping[venue.name] || {};
  const imageUrl  = details.imageUrl || venue.imageUrl;
  const venueName = details.title    || venue.name;

  // track selected forecast
  const [selectedMatchIndex, setSelectedMatchIndex] = useState(0);
  // paging state
  const [page, setPage] = useState(0);

  const today = new Date().toISOString().slice(0, 10);

  const selectedMatch = matches[selectedMatchIndex];

  // notify parent on initial mount & whenever selection changes
  useEffect(() => {
    onSelectDate && onSelectDate(selectedMatch);
  }, [selectedMatch, onSelectDate]);

  // lookup forecasts by ISO date string
  const matchMapByDate = useMemo(() => {
    return matches.reduce((acc, m, i) => {
      const iso = new Date(Number(m.forecast_date)).toISOString().slice(0,10);
      acc[iso] = { ...m, index: i };
      return acc;
    }, {});
  }, [matches]);

  // build calendar pages between startDate/endDate
  const calendars = useMemo(() => {
    if (!startDate || !endDate) return [];
    return monthsInRange(startDate, endDate).map(({ year, monthIndex }) => ({
      year,
      monthIndex,
      firstDow: firstWeekdayOfMonth(year, monthIndex),
      numDays:  daysInMonth(year, monthIndex),
    }));
  }, [startDate, endDate]);

  // If no calendar pages, render nothing
  if (calendars.length === 0) {
    return null;
  }

  const { year, monthIndex, firstDow, numDays } = calendars[page];
  const weekdays = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  return (
    <div className="venue-card">
      <img src={imageUrl} alt={venueName} className="venue-image" />
      <div className="card-content">
        <h3 className="venue-title">{venueName}</h3>

        <div className="match-info">
          <p className="match-date">
            {new Date(Number(selectedMatch.forecast_date)).toLocaleDateString()}
          </p>
          <p
            className="match-description"
            style={{ color: getMatchColor(selectedMatch.overall_score) }}
          >
            {selectedMatch.match_description} – {selectedMatch.overall_score}%
          </p>
        </div>

        {/* Calendar Heatmap */}
        <div className="calendar-container">
            <div className="calendar-nav">
              <button onClick={() => setPage(p => Math.max(p - 1, 0))}
                      disabled={page === 0}>‹ Prev</button>
              <span>{new Date(year, monthIndex).toLocaleString('default', { month:'long', year:'numeric' })}</span>
              <button onClick={() => setPage(p => Math.min(p + 1, calendars.length - 1))}
                      disabled={page === calendars.length - 1}>Next ›</button>
            </div>

            {/* weekday header */}
            <div className="calendar-grid header">
              {weekdays.map(d => <div key={d} className="calendar-cell header">{d}</div>)}
            </div>

            {/* day cells */}
            <div className="calendar-grid days">
              {/* leading blanks */}
              {Array(firstDow).fill(null).map((_, i) => (
                <div key={`blank-start-${i}`} className="calendar-cell empty" />
              ))}

              {/* real days */}
              {Array.from({ length: numDays }, (_, i) => {
                const day = i + 1;
                const iso = `${year}-${String(monthIndex+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;

                const match = matchMapByDate[iso];
                const isSel  = match?.index === selectedMatchIndex;
                const isPast = iso < today;
                const outOfRange = iso < startDate || iso > endDate;

                return (
                  <div
                    key={iso}
                    className={[
                      'calendar-cell','day',
                      match && !outOfRange ? 'has-match':'',
                      isSel ? 'selected':'',
                      (isPast || outOfRange) ? 'disabled':''
                    ].join(' ')}
                    style={match && !outOfRange ? { backgroundColor: getMatchColor(match.overall_score) } : {}}
                    onClick={match && !outOfRange ? () => setSelectedMatchIndex(match.index) : undefined}
                  >
                    {day}
                  </div>
                );
              })}

              {/* trailing blanks so rows are complete */}
              {(() => {
                const total = firstDow + numDays;
                const tail  = (7 - (total % 7)) % 7;
                return Array(tail).fill(null).map((_, i) => (
                  <div key={`blank-end-${i}`} className="calendar-cell empty" />
                ));
              })()}
            </div>
          </div>
      </div>
    </div>
  );
};

export default VenueCard;